import { useEffect, useState } from "react";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import swal from "sweetalert";

import { Button } from "../../elements/Button";
import CustomDropdownIndicator from "../../elements/CustomDropdownIndicator";
import buttoncss from "../../css/button.module.css";
import icrCss from "../../css/interCompanyEliminations.module.css";
import { ReactComponent as PlusPrimary } from "../../assets/icons/primary_plus.svg";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { ReactComponent as LeftArrowIcon } from "../../assets/icons/left_arrow.svg";
import { ReactComponent as DotIcon } from "../../assets/icons/dot.svg";
import { ReactComponent as DoubleArrowsIcon } from "../../assets/icons/doubleArrows.svg";
import { ReactComponent as DeleteRedIcon } from "../../assets/icons/delete_red.svg";
import { getICEWorkingsDropdown, getICEWorkings } from "./ice_mockdata";
import {
  getInterCompanyWorkingsApiHandler,
  setInterCompanyWorkingsApiHandler,
} from "../../../WorkSpace/Components/ApiServices/apiHandler";

// Format number: Indian commas, no decimals, negatives in brackets
const formatIndian = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const num = Number(val);
  if (isNaN(num)) return val;
  const absNum = Math.abs(Math.round(num));
  const formatted = absNum.toLocaleString("en-IN");
  return num < 0 ? `(${formatted})` : formatted;
};

export const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#f4faf5",
    borderColor: "#c2e6d1",
    color: "#b3c0bd",
    boxShadow: state.isFocused ? "0 0 0 1px #c2e6d1" : "none",
    borderWidth: "1px",
    borderRadius: "10px",
    "&:hover": {
      borderColor: "#b3c0bd",
    },
    minHeight: "38px", // ✅ base height
    height: "auto", // ✅ allow growth
  }),

  valueContainer: (provided) => ({
    ...provided,
    padding: "2px 8px",
    paddingTop: "1px",
    flexWrap: "wrap", // ✅ REQUIRED for multi-line chips
  }),

  input: (provided) => ({
    ...provided,
    color: "#b3c0bd",
  }),

  placeholder: (provided) => ({
    ...provided,
    color: "#b3c0bd",
    fontSize: "14px",
  }),

  singleValue: (provided) => ({
    ...provided,
    color: "#4d6d64",
    fontSize: "14px",
  }),

  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#e0f5e9",
    borderRadius: "5px",
    // border: "1px solid #c2e6d1",
    width: "fit-content",
  }),

  multiValueLabel: (provided) => ({
    ...provided,
    color: "#4d6d64",
  }),

  multiValueRemove: (provided) => ({
    ...provided,
    color: "#b3c0bd",
    ":hover": {
      backgroundColor: "#c2e6d1",
      color: "#4f665f",
    },
  }),

  indicatorsContainer: (provided) => ({
    ...provided,
    color: "#b3c0bd",
  }),

  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#1A4338",
    fontWeight: 400,
    ":hover": {
      color: "#1A4338",
      fontWeight: 400,
    },
  }),

  clearIndicator: (provided) => ({
    ...provided,
    color: "#b3c0bd",
    ":hover": {
      color: "#4f665f",
    },
  }),

  menu: (provided) => ({
    ...provided,
    backgroundColor: "#ffffff",
    border: "1px solid #c2e6d1",
    borderRadius: "6px",
    boxShadow: "0 0 0 1px #c2e6d1",
    marginTop: "6px",
    zIndex: 10,
  }),

  menuList: (provided) => ({
    ...provided,
    padding: "4px",
    borderRadius: "6px",
  }),

  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#c2e6d1"
      : state.isFocused
        ? "#f4faf5"
        : "transparent",
    color: "#4f665f",
    padding: "10px 12px",
    cursor: "pointer",
    fontSize: "14px",
    borderRadius: "8px",
  }),

  noOptionsMessage: (provided) => ({
    ...provided,
    color: "#b3c0bd",
    padding: "8px 12px",
  }),
};

const ICEWorkings = ({ setEliminationScreen, consolID }) => {
  const animatedComponents = makeAnimated();

  const [showAlltransactionRows, setShowAllTransactionRows] = useState(true);

  const [showAllBalancesRows, setShowAllBalancesRows] = useState(true);
  const [transactionOptions, setTransactionOptions] = useState([]);
  const [outstandingBalanceOptions, setOutstandingBalanceOptions] = useState(
    [],
  );
  const [mappedTransactionSelections, setMappedTransactionSelections] =
    useState([{ baseValues: [], comparisonValues: [] }]);

  const [mappedBalanceSelections, setMappedBalanceSelections] = useState([
    { baseValues: [], comparisonValues: [] },
  ]);

  const [currentYearTransactions, setCurrentYearTransactions] = useState([]);
  const [previousYearTransactions, setPreviousYearTransactions] = useState([]);
  const [currentYearBalances, setCurrentYearBalances] = useState([]);
  const [previousYearBalances, setPreviousYearBalances] = useState([]);

  useEffect(() => {
    getInterCompanyWorkings();
  }, []);

  const [activeTab, setActiveTab] = useState("transaction");

  const getInterCompanyWorkings = () => {
    getInterCompanyWorkingsApiHandler({
      consolID: consolID,
    })
      .then((res) => {
        if (res?.data?.success) {
          const RESP = res?.data;
           setTransactionOptions(RESP?.transactionOptions ?? []);
           setOutstandingBalanceOptions(RESP?.outstandingBalanceOptions ?? []);
         setCurrentYearTransactions(RESP?.currentYear?.transactions ?? []);
          setPreviousYearTransactions(RESP?.previousYear?.transactions ?? []);
          setCurrentYearBalances(RESP?.currentYear?.outstandingBalances ?? []);
          setPreviousYearBalances(RESP?.previousYear?.outstandingBalances ?? []);
          if (RESP?.mappedTransactions && RESP?.mappedTransactions.length > 0) {
            setMappedTransactionSelections(RESP.mappedTransactions);
          } else {
            setMappedTransactionSelections([
              { baseValues: [], comparisonValues: [] },
            ]);
          }

          if (
            RESP?.mappedOutstandingBalances &&
            RESP?.mappedOutstandingBalances.length > 0
          ) {
            setMappedBalanceSelections(RESP.mappedOutstandingBalances);
          } else {
            setMappedBalanceSelections([
              { baseValues: [], comparisonValues: [] },
            ]);
          }

          // setTransactionTableData(RESP?.transactions ?? []);
          // setOutstandingBalanceTableData(RESP?.outstandingBalances ?? []);
        } else {
          swal("Error", "Unable to get intercompany workings", "error");
        }
      })
      .catch(() => {
        swal("Error", "Unable to populate intercompany workings", "error");
      });
  };

  const handleAddRow = () => {
    setMappedTransactionSelections((prev) => [
      ...prev,
      { baseValues: null, comparisonValues: null },
    ]);
  };

  const handleDeleteRow = (index) => {
    setMappedTransactionSelections((prev) =>
      prev.filter((_, i) => i !== index),
    );
  };

  const handleAddBalancesRow = () => {
    setMappedBalanceSelections((prev) => [
      ...prev,
      { baseValues: null, comparisonValues: null },
    ]);
  };

  const handleDeleteBalancesRow = (index) => {
    setMappedBalanceSelections((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const payload = {
      mappedTransactions: mappedTransactionSelections
        .filter((r) => r.baseValues && r.comparisonValues)
        .map((r) => ({
          baseValues: r.baseValues,
          comparisonValues: r.comparisonValues,
        })),

      mappedOutstandingBalances: mappedBalanceSelections
        .filter((r) => r.baseValues && r.comparisonValues)
        .map((r) => ({
          baseValues: r.baseValues,
          comparisonValues: r.comparisonValues,
        })),
    };

    setInterCompanyWorkingsApiHandler({ consolID: consolID }, payload)
      .then((response) => {
        if (response?.data?.success === true) {
          getInterCompanyWorkings();
        } else {
          swal(
            "Error",
            response?.data?.error || "Unable to process data",
            "error",
          );
        }
      })
      .catch((error) => {
        swal(
          "Error",
          error?.response?.data?.error || "Unable to process data",
          "error",
        );
      });
  };

  const swapTransactionRow = (index) => {
    setMappedTransactionSelections((prev) => {
      const updated = [...prev];
      const { baseValues, comparisonValues } = updated[index];
      updated[index] = {
        baseValues: comparisonValues,
        comparisonValues: baseValues,
      };
      return updated;
    });
  };

  const swapBalanceRow = (index) => {
    setMappedBalanceSelections((prev) => {
      const updated = [...prev];
      const { baseValues, comparisonValues } = updated[index];
      updated[index] = {
        baseValues: comparisonValues,
        comparisonValues: baseValues,
      };
      return updated;
    });
  };

  return (
    <>
      <div className={icrCss.header}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            fontSize: "14px",
          }}
        >
          <span
            style={{ cursor: "pointer" }}
            onClick={() => setEliminationScreen(0)}
          >
            <span>
              <LeftArrowIcon height={12} style={{ marginBottom: "2px" }} />
            </span>
            <span style={{ color: "#4D6D64", marginLeft: "10px" }}>
              Inter company elimination
            </span>
          </span>
          <span>
            <DotIcon style={{ marginTop: "-3px" }} />
          </span>
          <span>View workings</span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            fontSize: "10px",
          }}
        >
          <Button
            icon={<Export height={16} />}
            btnColor={buttoncss.greyBg}
            label="Export Workings"
          />

          <Button
            btnColor={buttoncss.primary}
            label="Perform reconciliation"
            onClick={() => setEliminationScreen(2)}
          />
        </div>
      </div>
      <div  style={{ paddingTop: "20px" }}>
      <div style={{width: '100%', height: '100%', justifyContent: 'flex-start', alignItems: 'flex-start', display: 'inline-flex'}}>
      <div
        onClick={() => setActiveTab("transaction")}
        style={{
          padding: "8px 20px",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: activeTab === "transaction" ? "600" : "100",
          background: activeTab === "transaction" ? "#E4F2E6" : "transparent",
          borderBottom:
            activeTab === "transaction" ? "2px solid #2D8861" : "1px solid #eaeeed",
        }}
      >
        <div>Transaction Comparison</div>
      </div>

      <div
        onClick={() => setActiveTab("balance")}
        style={{
          padding: "8px 20px",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: activeTab === "balance" ? "600" : "100",
          background: activeTab === "balance" ? "#E4F2E6" : "transparent",
          borderBottom:
            activeTab === "balance" ? "2px solid #2D8861" : "1px solid #eaeeed",
        }}
      >
      <div>Balance Comparison</div>
    </div>
        <div style={{flex: '1 1 0', alignSelf: 'stretch', position: 'relative', borderBottom: '1px #eaeeed solid'}} />
    </div>

      <div className={icrCss.workingsPanelCtn}>
      {activeTab === "transaction" && (
        <>
        <div className={icrCss.workingsPanel} style={{ paddingLeft: "20px", paddingRight: "20px"}}>
          <div className={icrCss.panelHeader}>
            <label className="mb-0">Select transactions to be compared</label>
            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <Button
                class_={icrCss.panelButton}
                icon={<PlusPrimary />}
                btnColor={buttoncss.secondary}
                onClick={handleAddRow}
                label="Add New"
              />
              <Button
                class_={icrCss.panelButton}
                btnColor={buttoncss.primary}
                label="Save"
                onClick={handleSave}
              />
            </div>
          </div>

          <div className={icrCss.transactionInputsRowCtn} style={{paddingBottom: "20px"}}>
            {mappedTransactionSelections
              .slice(
                0,
                showAlltransactionRows ? mappedTransactionSelections.length : 4,
              )
              .map((_, index) => (
                <div key={index} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "60px", alignItems: "center", width: "calc(100% - 30px)", position: "relative", marginBottom: "10px" }}>
                  <Select
                    placeholder="Select transaction"
                    isMulti
                    options={transactionOptions.map((item) => ({
                      label: item,
                      value: item,
                    }))}
                    value={
                      mappedTransactionSelections[index]?.baseValues?.map(
                        (v) => ({
                          label: v,
                          value: v,
                        }),
                      ) || []
                    }
                    onChange={(selected) => {
                      setMappedTransactionSelections((prev) => {
                        const updated = [...prev];
                        updated[index].baseValues = selected
                          ? selected.map((s) => s.value)
                          : [];
                        return updated;
                      });
                    }}
                    classNamePrefix="select"
                    components={{
                      ...animatedComponents,
                      IndicatorSeparator: () => null,
                      DropdownIndicator: CustomDropdownIndicator,
                    }}
                      styles={{
                      ...customStyles,

                      container: (provided) => ({
                        ...provided,
                        width: "100%", 
                      }),
                    }}
                  />

                  <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", zIndex: 2, display: "flex" }}>
                    <DoubleArrowsIcon
                      style={{ minWidth: "30px", cursor: "pointer" }}
                      onClick={() => swapTransactionRow(index)}
                    />
                  </div>

                  <Select
                    placeholder="Select transaction"
                   // className={`w-100`}
                    isMulti
                    options={transactionOptions.map((item) => ({
                      label: item,
                      value: item,
                    }))}
                    value={
                      mappedTransactionSelections[index]?.comparisonValues?.map(
                        (v) => ({
                          label: v,
                          value: v,
                        }),
                      ) || []
                    }
                    onChange={(selected) => {
                      setMappedTransactionSelections((prev) => {
                        const updated = [...prev];
                        updated[index].comparisonValues = selected
                          ? selected.map((s) => s.value)
                          : [];
                        return updated;
                      });
                    }}
                    classNamePrefix="select"
                    components={{
                      ...animatedComponents,
                      IndicatorSeparator: () => null,
                      DropdownIndicator: CustomDropdownIndicator,
                    }}
                    styles={{
                      ...customStyles,
                      container: (provided) => ({
                        ...provided,
                        width: "100%", 
                      }),
                    }}
                  />

                  <div style={{ position: "absolute", right: "-30px", top: "50%", transform: "translateY(-50%)", display: "flex" }}>
                    <DeleteRedIcon
                      className={icrCss.deleteIcon}
                      style={{
                        minWidth: "20px",
                        cursor: "pointer",
                      }}
                      onClick={() => handleDeleteRow(index)}
                    />
                  </div>
                </div>
              ))}

            {mappedTransactionSelections.length > 4 && (
              <div className="text-right" style={{ width: "calc(100% - 30px)" }}>
                <button
                  style={{ border: "none" }}
                  className={`${buttoncss.secondary} btn-sm`}
                  onClick={() => setShowAllTransactionRows((prev) => !prev)}
                >
                  {showAlltransactionRows ? "View Less" : "View More"}
                </button>
              </div>
            )}
          </div>
          {/* <div
            className={`${icrCss.seperator} ${mappedTransactionSelections.length > 4 ? " mt-1 " : ""}`}
          ></div> */}
          <div style={{ display: "flex", gap: "20px", width: "100%", alignItems: "stretch"  }}>
          <div className={icrCss.card}>
          <div className={icrCss.cardHeader}>
            <span className={icrCss.dot}></span>
            Current Year
          </div>
          <div className={icrCss.panelTableCtn}>
            <table className={icrCss.panelTable}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Particulars</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th style={{ textAlign: "left" }}>Particulars</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th style={{ textAlign: "right" }}>Reconcile</th>
                  <th style={{ textAlign: "right" }}>Difference</th>
                </tr>
              </thead>
              <tbody>
                {currentYearTransactions.map((row, i) => (
                  <tr key={i}>
                    <td style={{ textAlign: "left" }}>
                      {Array.isArray(row.baseValues)
                        ? row.baseValues.map((item, idx) => (
                            <div key={idx}>{item}</div>
                          ))
                        : row.baseValues}
                    </td>
                    <td style={{ textAlign: "right" }}>{formatIndian(row.baseAmount)}</td>
                    <td style={{ textAlign: "left" }}>
                      {" "}
                      {Array.isArray(row.comparisonValues)
                        ? row.comparisonValues.map((item, idx) => (
                            <div key={idx}>{item}</div>
                          ))
                        : row.comparisonValues}
                    </td>
                    <td style={{ textAlign: "right" }}>{formatIndian(row.comparisonAmount)}</td>
                    <td style={{ textAlign: "right" }}>{formatIndian(row.reconcile)}</td>
                    <td
                      style={{
                        textAlign: "right",
                        color: row.difference !== 0 ? "#E30000" : "inherit",
                      }}
                    >
                      {formatIndian(row.difference)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>   
        </div>

  
       <div className={icrCss.card}>
          <div className={icrCss.cardPreviousHeader}>
            <span className={icrCss.dotPrevious}></span>
            Previous Year
          </div>

          <div className={icrCss.panelTableCtn}>
            <table className={icrCss.panelTable}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Particulars</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th style={{ textAlign: "left" }}>Particulars</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th style={{ textAlign: "right" }}>Reconcile</th>
                  <th style={{ textAlign: "right" }}>Difference</th>
                </tr>
              </thead>

              <tbody>
                {previousYearTransactions.map((row, i) => (
                  <tr key={i}>
                    <td style={{ textAlign: "left" }}>
                      {Array.isArray(row.comparisonValues)
                        ? row.comparisonValues.map((item, idx) => (
                            <div key={idx}>{item}</div>
                          ))
                        : row.comparisonValues}
                    </td>

                    <td style={{ textAlign: "right" }}>{formatIndian(row.comparisonAmount)}</td>

                    <td style={{ textAlign: "left" }}>
                      {Array.isArray(row.baseValues)
                        ? row.baseValues.map((item, idx) => (
                            <div key={idx}>{item}</div>
                          ))
                        : row.baseValues}
                    </td>

                    <td style={{ textAlign: "right" }}>{formatIndian(row.baseAmount)}</td>

                    <td style={{ textAlign: "right" }}>{formatIndian(row.reconcile)}</td>

                    <td
                      style={{
                        textAlign: "right",
                        color: row.difference !== 0 ? "#E30000" : "inherit",
                      }}
                    >
                      {formatIndian(row.difference)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
       </div>
        </div>
        </>  
       )}

     {activeTab === "balance" && (
      <>
        <div className={icrCss.workingsPanel} style={{ paddingLeft: "20px", paddingRight: "20px"}}>
          <div className={icrCss.panelHeader}>
            <label className="mb-0">Select balances to be compared</label>
            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <Button
                class_={icrCss.panelButton}
                icon={<PlusPrimary />}
                btnColor={buttoncss.secondary}
                onClick={handleAddBalancesRow}
                label="Add New"
              />
              <Button
                class_={icrCss.panelButton}
                btnColor={buttoncss.primary}
                label="Save"
                onClick={handleSave}
              />
            </div>
          </div>

          <div className={icrCss.transactionInputsRowCtn} style={{paddingBottom: "20px"}}>
            {mappedBalanceSelections
              .slice(
                0,
                showAllBalancesRows ? mappedBalanceSelections.length : 4,
              )
              .map((_, index) => (
                <div key={index} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "60px", alignItems: "center", width: "calc(100% - 30px)", position: "relative", marginBottom: "10px" }}>
                  <Select
                    placeholder="Select balances"
                    //className={`w-100`}
                    isMulti
                    options={outstandingBalanceOptions.map((item) => ({
                      label: item,
                      value: item,
                    }))}
                    value={
                      mappedBalanceSelections[index]?.baseValues?.map((v) => ({
                        label: v,
                        value: v,
                      })) || []
                    }
                    onChange={(selected) => {
                      setMappedBalanceSelections((prev) => {
                        const updated = [...prev];
                        updated[index].baseValues = selected
                          ? selected.map((s) => s.value)
                          : [];
                        return updated;
                      });
                    }}
                    classNamePrefix="select"
                    components={{
                      ...animatedComponents,
                      IndicatorSeparator: () => null,
                      DropdownIndicator: CustomDropdownIndicator,
                    }}
                    styles={{
                      ...customStyles,
                      container: (provided) => ({
                        ...provided,
                        width: "100%", 
                      }),
                    }}
                  />

                  <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", zIndex: 2, display: "flex" }}>
                    <DoubleArrowsIcon
                      style={{ minWidth: "30px", cursor: "pointer" }}
                      onClick={() => swapBalanceRow(index)}
                    />
                  </div>

                  <Select
                    placeholder="Select balances"
                    //className={`w-100`}
                    isMulti
                    options={outstandingBalanceOptions.map((item) => ({
                      label: item,
                      value: item,
                    }))}
                    value={
                      mappedBalanceSelections[index]?.comparisonValues?.map(
                        (v) => ({
                          label: v,
                          value: v,
                        }),
                      ) || []
                    }
                    onChange={(selected) => {
                      setMappedBalanceSelections((prev) => {
                        const updated = [...prev];
                        updated[index].comparisonValues = selected
                          ? selected.map((s) => s.value)
                          : [];
                        return updated;
                      });
                    }}
                    classNamePrefix="select"
                    components={{
                      ...animatedComponents,
                      IndicatorSeparator: () => null,
                      DropdownIndicator: CustomDropdownIndicator,
                    }}
                     styles={{
                      ...customStyles,
                      container: (provided) => ({
                        ...provided,
                        width: "100%", 
                      }),
                    }}
                  />

                  <div style={{ position: "absolute", right: "-30px", top: "50%", transform: "translateY(-50%)", display: "flex" }}>
                    <DeleteRedIcon
                      className={icrCss.deleteIcon}
                      style={{
                        minWidth: "20px",
                        cursor: "pointer",
                      }}
                      onClick={() => handleDeleteBalancesRow(index)}
                    />
                  </div>
                </div>
              ))}

            {mappedBalanceSelections.length > 4 && (
              <div className="text-right" style={{ width: "calc(100% - 30px)" }}>
                <button
                  style={{ border: "none" }}
                  className={`${buttoncss.secondary} btn-sm`}
                  onClick={() => setShowAllBalancesRows((prev) => !prev)}
                >
                  {showAllBalancesRows ? "View Less" : "View More"}
                </button>
              </div>
            )}
          </div>

          {/* <div
            className={`${icrCss.seperator} ${mappedBalanceSelections.length > 4 ? " mt-1 " : ""}`}
          ></div> */}

          <div style={{ display: "flex", gap: "20px", width: "100%", alignItems: "stretch"  }}>
            <div className={icrCss.card}>
            <div className={icrCss.cardHeader}>
              <span className={icrCss.dot}></span>
              Current Year
            </div>
          
          <div className={icrCss.panelTableCtn}>
            <table className={icrCss.panelTable}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Particulars</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th style={{ textAlign: "left" }}>Particulars</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th style={{ textAlign: "right" }}>Reconcile</th>
                  <th style={{ textAlign: "right" }}>Difference</th>
                </tr>
              </thead>
              <tbody>
                {currentYearBalances.map((row, i) => (
                  <tr key={i}>
                    <td style={{ textAlign: "left" }}>
                      {Array.isArray(row.baseValues)
                        ? row.baseValues.map((item, idx) => (
                            <div key={idx}>{item}</div>
                          ))
                        : row.baseValues}
                    </td>
                    <td style={{ textAlign: "right" }}>{formatIndian(row.baseAmount)}</td>
                    <td style={{ textAlign: "left" }}>
                      {" "}
                      {Array.isArray(row.comparisonValues)
                        ? row.comparisonValues.map((item, idx) => (
                            <div key={idx}>{item}</div>
                          ))
                        : row.comparisonValues}
                    </td>
                    <td style={{ textAlign: "right" }}>{formatIndian(row.comparisonAmount)}</td>
                    <td style={{ textAlign: "right" }}>{formatIndian(row.reconcile)}</td>
                    <td
                      style={{
                        textAlign: "right",
                        color: row.difference !== 0 ? "#E30000" : "inherit",
                      }}
                    >
                      {formatIndian(row.difference)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

         <div className={icrCss.card}>
          <div className={icrCss.cardPreviousHeader}>
            <span className={icrCss.dotPrevious}></span>
            Previous Year
          </div>

          <div className={icrCss.panelTableCtn}>
            <table className={icrCss.panelTable}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Particulars</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th style={{ textAlign: "left" }}>Particulars</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th style={{ textAlign: "right" }}>Reconcile</th>
                  <th style={{ textAlign: "right" }}>Difference</th>
                </tr>
              </thead>
              <tbody>
                {previousYearBalances.map((row, i) => (
                  <tr key={i}>
                    <td style={{ textAlign: "left" }}>
                      {Array.isArray(row.baseValues)
                        ? row.baseValues.map((item, idx) => (
                            <div key={idx}>{item}</div>
                          ))
                        : row.baseValues}
                    </td>
                    <td style={{ textAlign: "right" }}>{formatIndian(row.baseAmount)}</td>
                    <td style={{ textAlign: "left" }}>
                      {" "}
                      {Array.isArray(row.comparisonValues)
                        ? row.comparisonValues.map((item, idx) => (
                            <div key={idx}>{item}</div>
                          ))
                        : row.comparisonValues}
                    </td>
                    <td style={{ textAlign: "right" }}>{formatIndian(row.comparisonAmount)}</td>
                    <td style={{ textAlign: "right" }}>{formatIndian(row.reconcile)}</td>
                    <td
                      style={{
                        textAlign: "right",
                        color: row.difference !== 0 ? "#E30000" : "inherit",
                      }}
                    >
                      {formatIndian(row.difference)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
       </div>
      </div>
      </>
     )}
    </div>
    </div>
    </>
  );
};

export default ICEWorkings;
